import React, { useEffect, useState } from 'react';
import './Ads.css';
import { apiGet } from '../api';

const TopAd = ({ language = 'en', onAdStatusChange }) => {
  const [ad, setAd] = useState(null);
  const [adBlockDetected, setAdBlockDetected] = useState(false);
  const [showAdBlockPopup, setShowAdBlockPopup] = useState(false);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        const hash = window.location.hash || '';
        const m = hash.match(/^#\/blog\/(en|ml)\/(.+)$/);

        let selectedAd = null;
        if (m) {
          // Blog detail page: fetch targeted top ad for this post
          const slug = decodeURIComponent(m[2]);
          try {
            const post = await apiGet(`/api/posts/${encodeURIComponent(slug)}`, { locale: language });
            if (post?.id) {
              const adsResp = await apiGet('/api/ads', { locale: language, zone: 'top_ad', placement: 'blog_detail', postId: String(post.id), limit: 1 });
              const items = Array.isArray(adsResp?.items) ? adsResp.items : [];
              selectedAd = items[0] || null;
            }
          } catch (_e) { /* ignore */ }
        }

        // Fallback to home top ad if no blog-specific ad found or not on blog detail
        if (!selectedAd) {
          const adsResp = await apiGet('/api/ads', { locale: language, zone: 'top_ad', placement: 'home', limit: 1 });
          const items = Array.isArray(adsResp?.items) ? adsResp.items : [];
          selectedAd = items[0] || null;
        }

        if (!ignore) {
          setAd(selectedAd);
          // Always show ad space (hasTopAd = true) unless adblocker blocks it
          if (onAdStatusChange) onAdStatusChange(true);
        }
      } catch (_) {
        if (!ignore) {
          setAd(null);
          if (onAdStatusChange) onAdStatusChange(true);
        }
      }
    };

    load();
    const onHash = () => { load(); };
    window.addEventListener('hashchange', onHash);
    return () => { ignore = true; window.removeEventListener('hashchange', onHash); };
  }, [language, onAdStatusChange]);

  // Detect adblocker
  useEffect(() => {
    const detectAdBlock = async () => {
      try {
        // Create a bait element that adblockers typically block
        const bait = document.createElement('div');
        bait.className = 'ad-banner ad-placement ads advertisement';
        bait.style.cssText = 'position:absolute;top:-1px;left:-1px;width:1px;height:1px;';
        document.body.appendChild(bait);
        
        // Wait a bit for adblocker to act
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const isBlocked = bait.offsetHeight === 0 || 
                         window.getComputedStyle(bait).display === 'none' ||
                         bait.offsetParent === null;
        
        document.body.removeChild(bait);
        
        if (isBlocked) {
          setAdBlockDetected(true);
          // Hide ad space when adblocker detected
          if (onAdStatusChange) onAdStatusChange(false);
          // Show popup after 2 seconds if adblocker detected
          setTimeout(() => setShowAdBlockPopup(true), 2000);
        }
      } catch (e) {
        // Ignore errors
      }
    };
    
    detectAdBlock();
  }, [onAdStatusChange]);

  // Don't render ad space if adblocker detected
  if (adBlockDetected) {
    return (
      <>
        {showAdBlockPopup && (
          <div className="adblock-popup" onClick={() => setShowAdBlockPopup(false)}>
            <div className="adblock-popup-content" onClick={(e) => e.stopPropagation()}>
              <button className="adblock-popup-close" onClick={() => setShowAdBlockPopup(false)}>×</button>
              <h3>🚫 Ad Blocker Detected</h3>
              <p>
                {language === 'en' 
                  ? 'We noticed you\'re using an ad blocker. Ads help us keep this content free. Please consider disabling your ad blocker for this site to support us.'
                  : 'നിങ്ങൾ ആഡ് ബ്ലോക്കർ ഉപയോഗിക്കുന്നുണ്ടെന്ന് ഞങ്ങൾ കണ്ടെത്തി. പരസ്യങ്ങൾ ഈ ഉള്ളടക്കം സൗജന്യമായി നിലനിർത്താൻ ഞങ്ങളെ സഹായിക്കുന്നു. ഞങ്ങളെ പിന്തുണയ്ക്കാൻ ഈ സൈറ്റിനായി നിങ്ങളുടെ ആഡ് ബ്ലോക്കർ പ്രവർത്തനരഹിതമാക്കുന്നത് പരിഗണിക്കുക.'}
              </p>
              <button className="adblock-popup-btn" onClick={() => setShowAdBlockPopup(false)}>
                {language === 'en' ? 'Got it' : 'മനസ്സിലായി'}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="ad-top" role="complementary" aria-label="Top Advertisement">
        {ad ? (
          <a href={ad.ctaUrl || '#'} target={ad.ctaUrl ? '_blank' : undefined} rel={ad.ctaUrl ? 'noopener noreferrer' : undefined} className="ad-top-inner">
            <img src={ad.imageUrl || 'https://via.placeholder.com/1200x240/2d5016/ffffff?text=Advertisement'} alt={ad.title || 'Advertisement'} />
            <div className="ad-top-content">
              {ad.adText && <span className="ad-label">{ad.adText}</span>}
              <div className="ad-top-title">{ad.title}</div>
              {ad.ctaText && <div className="ad-top-cta">{ad.ctaText}</div>}
            </div>
          </a>
        ) : (
          <div className="ad-label">Display Your Ads here</div>
        )}
      </div>
      
      {showAdBlockPopup && (
        <div className="adblock-popup" onClick={() => setShowAdBlockPopup(false)}>
          <div className="adblock-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="adblock-popup-close" onClick={() => setShowAdBlockPopup(false)}>×</button>
            <h3>🚫 Ad Blocker Detected</h3>
            <p>
              {language === 'en' 
                ? 'We noticed you\'re using an ad blocker. Ads help us keep this content free. Please consider disabling your ad blocker for this site to support us.'
                : 'നിങ്ങൾ ആഡ് ബ്ലോക്കർ ഉപയോഗിക്കുന്നുണ്ടെന്ന് ഞങ്ങൾ കണ്ടെത്തി. പരസ്യങ്ങൾ ഈ ഉള്ളടക്കം സൗജന്യമായി നിലനിർത്താൻ ഞങ്ങളെ സഹായിക്കുന്നു. ഞങ്ങളെ പിന്തുണയ്ക്കാൻ ഈ സൈറ്റിനായി നിങ്ങളുടെ ആഡ് ബ്ലോക്കർ പ്രവർത്തനരഹിതമാക്കുന്നത് പരിഗണിക്കുക.'}
            </p>
            <button className="adblock-popup-btn" onClick={() => setShowAdBlockPopup(false)}>
              {language === 'en' ? 'Got it' : 'മനസ്സിലായി'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TopAd;
